/**
 * PJH_BRIDGE: CT-1881 - Token Storage Utility for Dual-Token Authentication
 *
 * Manages both WasteHero JWT (for GraphQL/PJH) and IAM tokens (for microservices).
 * Remove PJH token management after all modules fully migrated to microservices.
 *
 * Storage Strategy:
 * - WasteHero JWT: localStorage (existing behavior, persists across sessions)
 * - IAM Access Token: sessionStorage (cleared on tab close for security)
 * - IAM Refresh Token: httpOnly cookie (set by backend, not accessible by JS)
 * - Remember Me: localStorage (persists user preference)
 */

// Storage keys
const WASTEHERO_TOKEN_KEY = 'reactQLJWT'
const IAM_ACCESS_TOKEN_KEY = 'iamAccessToken'
const IAM_REFRESH_TOKEN_KEY = 'iamRefreshToken'
const REMEMBER_ME_KEY = 'rememberMe'

export const TokenStorage = {
  // WasteHero JWT Token (localStorage - existing behavior)
  getWasteheroToken: (): string | null => {
    return localStorage.getItem(WASTEHERO_TOKEN_KEY)
  },

  setWasteheroToken: (token: string): void => {
    localStorage.setItem(WASTEHERO_TOKEN_KEY, token)
  },

  removeWasteheroToken: (): void => {
    localStorage.removeItem(WASTEHERO_TOKEN_KEY)
  },

  // IAM Access Token (conditional storage based on Remember Me)
  // - If rememberMe = true: localStorage (persists across sessions)
  // - If rememberMe = false: sessionStorage (cleared on tab close)
  getIAMAccessToken: (): string | null => {
    const rememberMe = TokenStorage.getRememberMe()
    if (rememberMe) {
      return localStorage.getItem(IAM_ACCESS_TOKEN_KEY)
    }
    return sessionStorage.getItem(IAM_ACCESS_TOKEN_KEY)
  },

  setIAMAccessToken: (token: string): void => {
    const rememberMe = TokenStorage.getRememberMe()
    if (rememberMe) {
      localStorage.setItem(IAM_ACCESS_TOKEN_KEY, token)
      // Clear from sessionStorage to avoid stale data
      sessionStorage.removeItem(IAM_ACCESS_TOKEN_KEY)
    } else {
      sessionStorage.setItem(IAM_ACCESS_TOKEN_KEY, token)
      // Clear from localStorage to avoid stale data
      localStorage.removeItem(IAM_ACCESS_TOKEN_KEY)
    }
  },

  removeIAMAccessToken: (): void => {
    // Remove from both storages to ensure cleanup
    sessionStorage.removeItem(IAM_ACCESS_TOKEN_KEY)
    localStorage.removeItem(IAM_ACCESS_TOKEN_KEY)
  },

  // IAM Refresh Token (stored in JS, same location as access token)
  getIAMRefreshToken: (): string | null => {
    const rememberMe = TokenStorage.getRememberMe()
    if (rememberMe) {
      return localStorage.getItem(IAM_REFRESH_TOKEN_KEY)
    }
    return sessionStorage.getItem(IAM_REFRESH_TOKEN_KEY)
  },

  setIAMRefreshToken: (token: string): void => {
    const rememberMe = TokenStorage.getRememberMe()
    if (rememberMe) {
      localStorage.setItem(IAM_REFRESH_TOKEN_KEY, token)
      sessionStorage.removeItem(IAM_REFRESH_TOKEN_KEY)
    } else {
      sessionStorage.setItem(IAM_REFRESH_TOKEN_KEY, token)
      localStorage.removeItem(IAM_REFRESH_TOKEN_KEY)
    }
  },

  removeIAMRefreshToken: (): void => {
    sessionStorage.removeItem(IAM_REFRESH_TOKEN_KEY)
    localStorage.removeItem(IAM_REFRESH_TOKEN_KEY)
  },

  // Remember Me Preference (localStorage)
  getRememberMe: (): boolean => {
    return localStorage.getItem(REMEMBER_ME_KEY) === 'true'
  },

  setRememberMe: (remember: boolean): void => {
    localStorage.setItem(REMEMBER_ME_KEY, String(remember))
  },

  /**
   * Store tokens from login response.
   * Called after successful tokenAuth mutation.
   */
  storeLoginTokens: (
    wasteheroToken: string,
    iamAccessToken?: string | null,
    rememberMe?: boolean
  ): void => {
    TokenStorage.setWasteheroToken(wasteheroToken)

    // Set rememberMe preference first, as it affects IAM token storage location
    if (rememberMe !== undefined) {
      TokenStorage.setRememberMe(rememberMe)
    }

    if (iamAccessToken) {
      TokenStorage.setIAMAccessToken(iamAccessToken)
    }

    // Note: IAM refresh token is set as httpOnly cookie by backend
  },

  /**
   * Clear all authentication tokens.
   * Called on logout or when IAM token refresh fails.
   */
  clearAll: (): void => {
    localStorage.removeItem(WASTEHERO_TOKEN_KEY)
    // Clear IAM tokens from both storages
    sessionStorage.removeItem(IAM_ACCESS_TOKEN_KEY)
    localStorage.removeItem(IAM_ACCESS_TOKEN_KEY)
    sessionStorage.removeItem(IAM_REFRESH_TOKEN_KEY)
    localStorage.removeItem(IAM_REFRESH_TOKEN_KEY)
    localStorage.removeItem(REMEMBER_ME_KEY)
  },

  /**
   * Check if user has any valid authentication.
   */
  hasAuthentication: (): boolean => {
    return Boolean(TokenStorage.getWasteheroToken())
  },

  /**
   * Check if IAM token is available.
   * Used to determine if X-IAM-Token header should be sent.
   */
  hasIAMToken: (): boolean => {
    return Boolean(TokenStorage.getIAMAccessToken())
  },
}

export default TokenStorage
